import { Popper } from '@mui/material'
import {
  GridPreferencePanelsValue,
  gridPreferencePanelStateSelector,
  useGridApiContext,
  useGridApiEventHandler,
} from '@mui/x-data-grid'
import { DismissableLayer } from '@radix-ui/react-dismissable-layer'
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react'
import { PanelProvider, usePanelContext } from '~/components/tables/FilterPanelProvider'

// Separate the inner content to access context
const PanelContent = forwardRef<HTMLDivElement, CustomPanelProps>((props, ref) => {
  const apiRef = useGridApiContext()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const panelState = gridPreferencePanelStateSelector(apiRef.current.state)
  const panelRef = useRef<HTMLDivElement>(null)
  const panelContext = usePanelContext()
  const isSelectOpen = panelContext?.isSelectOpen

  const findAnchorElement = useCallback(() => {
    const gridRoot = apiRef.current.rootElementRef?.current
    if (!gridRoot) {
      return null
    }

    return gridRoot.querySelector('.MuiDataGrid-columnHeaders') as HTMLElement
  }, [apiRef])

  useGridApiEventHandler(apiRef, 'preferencePanelOpen', () => {
    const anchor = findAnchorElement()
    if (anchor) {
      setAnchorEl(anchor)
    }
  })

  useGridApiEventHandler(apiRef, 'preferencePanelClose', () => {
    setAnchorEl(null)
  })

  useEffect(() => {
    if (panelState.open && !anchorEl) {
      const anchor = findAnchorElement()
      if (anchor) {
        setAnchorEl(anchor)
      }
    }
  }, [panelState.open, anchorEl, findAnchorElement])

  const handleDismiss = useCallback(() => {
    if (!isSelectOpen) {
      apiRef.current.hidePreferences()
    }
  }, [apiRef, isSelectOpen])

  // Only prevent pointer down outside when select is open
  const handlePointerDownOutside = useCallback(
    (event: Event) => {
      const target = event.target as HTMLElement
      const isClickingInsidePanel = target.closest('[data-select-container]')

      if (isSelectOpen && isClickingInsidePanel) {
        event.preventDefault()
      }
    },
    [isSelectOpen],
  )

  if (!panelState.open) {
    return null
  }

  return (
    <DismissableLayer onDismiss={handleDismiss} onPointerDownOutside={handlePointerDownOutside}>
      <Popper
        ref={ref}
        anchorEl={anchorEl}
        className="z-10"
        modifiers={[
          {
            name: 'flip',
            enabled: false,
          },
          {
            name: 'preventOverflow',
            enabled: true,
          },
          {
            name: 'offset',
            options: {
              offset: [4, 4],
            },
          },
        ]}
        open={panelState.open}
        placement={
          panelState.openedPanelValue === GridPreferencePanelsValue.columns
            ? 'bottom-end'
            : 'bottom-start'
        }
      >
        <div
          ref={panelRef}
          className="min-w-[300px] rounded bg-white shadow-lg"
          data-select-container
        >
          {props.children}
        </div>
      </Popper>
    </DismissableLayer>
  )
})

PanelContent.displayName = 'PanelContent'

interface CustomPanelProps {
  children?: React.ReactNode
}

// Main component just wraps content with provider
const CustomPanel = forwardRef<HTMLDivElement, CustomPanelProps>((props, ref) => {
  return (
    <PanelProvider>
      <PanelContent ref={ref} {...props} />
    </PanelProvider>
  )
})

CustomPanel.displayName = 'CustomPanel'

export default CustomPanel
